import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Building, Mail, Phone, Calendar, FileText, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get auth headers
const getAuthHeaders = () => {
  const stored = sessionStorage.getItem('assistant');
  if (stored) {
    try {
      const assistant = JSON.parse(stored);
      return { 'x-assistant-session': assistant.id };
    } catch (e) {}
  }
  return {};
};

const AssignedClientsPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/assistant/clients`, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setClients(data.clients || []);
      } else if (res.status === 401) {
        navigate('/assistant-login');
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(client => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      client.company_name?.toLowerCase().includes(query) ||
      client.client_name?.toLowerCase().includes(query) ||
      client.client_id?.toLowerCase().includes(query)
    );
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#10B981';
      case 'pending': return '#F59E0B';
      case 'inactive': return '#6B7280';
      default: return '#6B7280';
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F8F9FA', padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#6B7280' }}>Loading clients...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F8F9FA' }}>
      {/* Header */}
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E7EB', padding: '20px 32px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={() => navigate('/a-dashboard')}
                style={{
                  padding: '8px',
                  background: 'transparent',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ArrowLeft size={18} color="#6B7280" />
              </button>
              <div>
                <h1 style={{ color: '#0A0A0A', fontSize: '24px', fontWeight: '600', margin: '0 0 4px 0' }}>
                  Assigned Clients
                </h1>
                <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
                  View-only access to clients managed by your executives
                </p>
              </div>
            </div>
          </div>

          {/* Search */}
          <div style={{ position: 'relative', maxWidth: '400px' }}>
            <Search size={18} color="#9CA3AF" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              placeholder="Search by company, name, or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px' }}>
        {filteredClients.length === 0 ? (
          <div style={{
            background: '#FFFFFF',
            borderRadius: '12px',
            border: '1px solid #E5E7EB',
            padding: '48px',
            textAlign: 'center'
          }}>
            <AlertCircle size={48} color="#9CA3AF" style={{ margin: '0 auto 16px' }} />
            <h3 style={{ color: '#374151', fontSize: '16px', fontWeight: '500', margin: '0 0 8px 0' }}>
              {searchQuery ? 'No Clients Found' : 'No Assigned Clients'}
            </h3>
            <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
              {searchQuery 
                ? 'Try adjusting your search query' 
                : 'Your executives have not been assigned any clients yet'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
            {filteredClients.map(client => (
              <div
                key={client.client_id}
                style={{
                  background: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  padding: '24px',
                  transition: 'box-shadow 0.2s',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)'}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
              >
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#FFFFFF',
                      fontSize: '18px',
                      fontWeight: '600'
                    }}>
                      {client.company_name?.substring(0, 2).toUpperCase() || client.client_name?.substring(0, 2).toUpperCase() || 'CL'}
                    </div>
                    <div>
                      <h3 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', margin: '0 0 4px 0' }}>
                        {client.company_name || client.client_name || 'Unnamed Client'}
                      </h3>
                      <span style={{ fontSize: '12px', color: '#6B7280', fontFamily: 'monospace' }}>
                        {client.client_id}
                      </span>
                    </div>
                  </div>
                  <div style={{
                    padding: '4px 10px',
                    borderRadius: '12px',
                    background: `${getStatusColor(client.status)}20`,
                    color: getStatusColor(client.status),
                    fontSize: '12px',
                    fontWeight: '500',
                    textTransform: 'capitalize'
                  }}>
                    {client.status || 'active'}
                  </div>
                </div>

                {/* Client Info */}
                <div style={{ marginBottom: '16px' }}>
                  {client.client_name && client.company_name && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <Building size={16} color="#9CA3AF" />
                      <span style={{ fontSize: '14px', color: '#374151' }}>{client.client_name}</span>
                    </div>
                  )}
                  {client.email && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <Mail size={16} color="#9CA3AF" />
                      <a href={`mailto:${client.email}`} style={{ fontSize: '14px', color: '#3B82F6', textDecoration: 'none' }}>
                        {client.email}
                      </a>
                    </div>
                  )}
                  {client.phone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <Phone size={16} color="#9CA3AF" />
                      <a href={`tel:${client.phone}`} style={{ fontSize: '14px', color: '#3B82F6', textDecoration: 'none' }}>
                        {client.phone}
                      </a>
                    </div>
                  )}
                </div>

                {/* Stats */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px',
                  padding: '16px 0',
                  borderTop: '1px solid #E5E7EB'
                }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                      <FileText size={14} color="#9CA3AF" />
                      <span style={{ fontSize: '12px', color: '#6B7280' }}>To-Dos</span>
                    </div>
                    <p style={{ fontSize: '20px', fontWeight: '600', color: '#0A0A0A', margin: 0 }}>
                      {client.todos?.length || 0}
                    </p>
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                      <Calendar size={14} color="#9CA3AF" />
                      <span style={{ fontSize: '12px', color: '#6B7280' }}>Onboarded</span>
                    </div>
                    <p style={{ fontSize: '14px', fontWeight: '500', color: '#374151', margin: 0 }}>
                      {client.onboarding_date 
                        ? new Date(client.onboarding_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                        : 'N/A'
                      }
                    </p>
                  </div>
                </div>

                {/* Executive Assignment */}
                {client.executive_name && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: '#F9FAFB',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB'
                  }}>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>
                      Managed by
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                      {client.executive_name}
                    </div>
                  </div>
                )}

                {/* View-Only Notice */}
                <div style={{
                  marginTop: '16px',
                  padding: '8px 12px',
                  background: '#FEF3C7',
                  border: '1px solid #FDE68A',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#92400E',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <AlertCircle size={14} />
                  View-only access
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary */}
        {filteredClients.length > 0 && (
          <div style={{
            marginTop: '24px',
            padding: '16px 24px',
            background: '#FFFFFF',
            borderRadius: '12px',
            border: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div>
              <p style={{ color: '#6B7280', fontSize: '14px', margin: '0 0 4px 0' }}>
                Total Assigned Clients
              </p>
              <p style={{ color: '#0A0A0A', fontSize: '24px', fontWeight: '600', margin: 0 }}>
                {filteredClients.length}
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ color: '#6B7280', fontSize: '14px', margin: '0 0 4px 0' }}>
                Active Clients
              </p>
              <p style={{ color: '#10B981', fontSize: '24px', fontWeight: '600', margin: 0 }}>
                {filteredClients.filter(c => c.status === 'active').length}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignedClientsPage;
